import React, { useEffect, useMemo, useRef, useState } from 'react';
import { path, identity } from 'ramda';
import { useTranslations } from 'js/hooks/i18n';
import { Loader } from 'js/components/common/Loader';
import { tagTypeService } from 'js/services/tagType.service';
import { useUserSelector } from 'js/store/hooks';
import { TAG_TYPE } from 'js/components/system/TagManager/constants';

import { INTEGRATIONS } from './constants';
import { useDesktopIntegration } from './components/GeoComplyDesktop';
import { useOobeeIntegration } from './components/GeoComplyOobee';
import { useNativeIntegration } from './components/GeoComplyNative';
import { useSolusIntegration } from './components/GeoComplySolus';
import * as hooks from './hooks';
import * as constants from './constants';

import style from './GeoComply.scss';

export const GeoComply = ({ config }) => {
    const t = useTranslations();
    const userId = useUserSelector(path(['userDetails', 'userId']));

    const {
        // desktop config
        enableDesktop,
        installerKey,
        installerBaseURL,
        installerVersion,
        clientURL,
        environmentID,
        // mobile config
        enableMobile,
        disableStoreAutoLaunch,
        mobileClientURL,
        oobeeURL,
        // native config
        enableNative,
        // solus config
        enableSolus,
        solusClientURL,
        // general config
        useTransparentPopup,
        identifierType,
        skipBrowserGeolocation = false
    } = config;

    const triggerTimestampRef = useRef(null);
    const [transactionID, setTransactionID] = useState(null);
    const [nextTime, setNextTime] = useState(0);
    const [license, setLicense] = useState(null);
    const [sdk, setSDK] = useState(null);
    const [showOverlay, setShowOverlay] = useState(false);
    const cancelRef = useRef(false);
    const reasonRef = useRef(constants.GEOLOCATION_REASON.UNDEFINED);

    const runBrowserGeolocation = hooks.useRunBrowserGeolocation({ showOverlay, useTransparentPopup });

    const selectedIntegration = hooks.useSelectedIntegration({
        enableDesktop,
        enableMobile,
        enableNative,
        enableSolus
    });

    const geolocationType = useMemo(() => {
        switch (selectedIntegration) {
            case INTEGRATIONS.GEOCOMPLY_DESKTOP:
                return 'geocomply';
            case INTEGRATIONS.GEOCOMPLY_OOBEE:
                return 'geocomply';
            case INTEGRATIONS.GEOCOMPLY_NATIVE:
                return 'geocomply';
            case INTEGRATIONS.GEOCOMPLY_SOLUS:
                return 'geocomply_browser';
            default:
                return identity;
        }
    }, [
        selectedIntegration
    ]);

    /**
     * This should be called when geolocation has to be scheduled.
     */
    const triggerGeolocation = hooks.useTriggerGeolocation({
        triggerTimestampRef,
        setLicense,
        setTransactionID,
        setNextTime,
        setShowOverlay,
        cancelRef,
        geolocationType,
        reasonRef
    });

    /**
     * This receives geoPacket from the geolocation provider.
     */
    const receivePacket = hooks.useReceivePacket({
        triggerGeolocation,
        setShowOverlay,
        useTransparentPopup,
        triggerTimestampRef
    });

    /**
     * This should be called when SDK is downloaded and initialized.
     * It triggers runBrowserGeolocation and then triggerGeolocation.
     */
    const onConnect = hooks.useOnConnect({
        setShowOverlay,
        triggerGeolocation,
        runBrowserGeolocation,
        skipBrowserGeolocation,
        geolocationType
    });

    /**
     * Desktop integration of GeoComply.
     */
    const { downloadClientSDK, requestDesktopPacket } = useDesktopIntegration({
        sdk,
        enable: selectedIntegration === INTEGRATIONS.GEOCOMPLY_DESKTOP,
        installerKey,
        installerBaseURL,
        installerVersion,
        clientURL,
        environmentID,
        license,
        identifierType,
        transactionID,
        userId,
        onConnect,
        useTransparentPopup,
        setShowOverlay,
        receivePacket,
        triggerGeolocation,
        reasonRef
    });

    /**
     * Mobile integration of GeoComply.
     */
    const { downloadOobeeSDK, requestOobeePacket } = useOobeeIntegration({
        sdk,
        enable: selectedIntegration === INTEGRATIONS.GEOCOMPLY_OOBEE,
        license,
        mobileClientURL,
        oobeeURL,
        identifierType,
        disableStoreAutoLaunch,
        transactionID,
        userId,
        onConnect,
        useTransparentPopup,
        setShowOverlay,
        receivePacket,
        triggerGeolocation,
        reasonRef
    });

    /**
     * Mobile integration of GeoComply.
     */
    const { downloadSolusSDK, requestSolusPacket } = useSolusIntegration({
        sdk,
        enable: selectedIntegration === INTEGRATIONS.GEOCOMPLY_SOLUS,
        license,
        solusClientURL,
        oobeeURL,
        identifierType,
        transactionID,
        userId,
        onConnect,
        useTransparentPopup,
        setShowOverlay,
        receivePacket,
        triggerGeolocation,
        reasonRef
    });

    /**
     * Native integration of GeoComply.
     */
    const { initClientSDK, requestNativePacket } = useNativeIntegration({
        sdk,
        enable: selectedIntegration === INTEGRATIONS.GEOCOMPLY_NATIVE,
        license,
        identifierType,
        transactionID,
        userId,
        onConnect,
        useTransparentPopup,
        setShowOverlay,
        receivePacket,
        triggerGeolocation,
        reasonRef
    });

    const downloadSDK = useMemo(() => {
        switch (selectedIntegration) {
            case INTEGRATIONS.GEOCOMPLY_DESKTOP:
                return downloadClientSDK;
            case INTEGRATIONS.GEOCOMPLY_OOBEE:
                return downloadOobeeSDK;
            case INTEGRATIONS.GEOCOMPLY_NATIVE:
                return initClientSDK;
            case INTEGRATIONS.GEOCOMPLY_SOLUS:
                return downloadSolusSDK;
            default:
                return identity;
        }
    }, [
        selectedIntegration,
        downloadClientSDK,
        downloadOobeeSDK,
        downloadSolusSDK,
        initClientSDK
    ]);

    const requestPacket = useMemo(() => {
        switch (selectedIntegration) {
            case INTEGRATIONS.GEOCOMPLY_DESKTOP:
                return requestDesktopPacket;
            case INTEGRATIONS.GEOCOMPLY_OOBEE:
                return requestOobeePacket;
            case INTEGRATIONS.GEOCOMPLY_NATIVE:
                return requestNativePacket;
            case INTEGRATIONS.GEOCOMPLY_SOLUS:
                return requestSolusPacket;
            default:
                return identity;
        }
    }, [
        requestSolusPacket,
        requestOobeePacket,
        selectedIntegration,
        requestNativePacket,
        requestDesktopPacket
    ]);

    /**
     * Initial call checks whether there is a need to perform a Geofencing check.
     * If yes, downloads SDK and shows the overlay.
     */
    const initialCall = hooks.useInitialCall({
        geolocationType,
        setSDK,
        setShowOverlay,
        downloadSDK,
        useTransparentPopup
    });

    /**
     * Runs once there is a need to actually call GeoComply.
     */
    hooks.useScheduler({
        sdk,
        nextTime,
        license,
        transactionID,
        userId,
        identifierType,
        setTransactionID,
        setNextTime,
        setLicense,
        triggerGeolocation,
        requestPacket,
        triggerTimestampRef
    });

    /**
     * Subscribe to IP change event from ip-changed subscription.
     */
    hooks.useOnIpChange({
        sdk,
        nextTime,
        triggerGeolocation,
        setTransactionID,
        triggerTimestampRef,
        setLicense,
        setNextTime
    });

    /**
     * Run once OGW is reconnected due to disconnect.
     */
    hooks.useOnReconnect({
        sdk,
        nextTime,
        triggerGeolocation,
        setTransactionID,
        triggerTimestampRef,
        setLicense,
        setNextTime
    });

    /**
     * Run once SDK has been downloaded.
     */
    useEffect(() => {
        if (selectedIntegration === INTEGRATIONS.DISABLED) {
            return;
        }

        if (userId) {
            cancelRef.current = false;

            initialCall();
        }

        return () => {
            cancelRef.current = true;

            // unset current timeout
            triggerTimestampRef.current = null;
            setTransactionID(null);
            setLicense(null);
            setNextTime(0);
            setShowOverlay(false);
            setSDK(null);
        };
    }, [
        userId,
        cancelRef,
        initialCall,
        selectedIntegration
    ]);

    /**
     * Enable listener to subscription to ACLRuleFailure
     */
    hooks.useOnACLRuleFailure({
        sdk,
        nextTime,
        setLicense,
        setNextTime,
        setShowOverlay,
        useTransparentPopup,
        setTransactionID,
        triggerTimestampRef,
        triggerGeolocation
    });

    /**
     * Show loading only if it is configured, if player is logged in and only first time.
     */
    if (showOverlay) {
        return <>
            <div className={style.geocomply}>
                <div
                    className={style.geocomplyOverlayTitle}>{t('playtech.mobile.system.geocomply.popups.loading.title')}</div>
                <div className={style.geocomplyOverlay}>
                    {t('playtech.mobile.system.geocomply.popups.loading.content')}
                    <br />
                    <br />
                    <br />
                    <br />
                    <Loader />
                </div>
            </div>
        </>;
    }

    return null;
};

tagTypeService.registerTagType({ [TAG_TYPE.geoComply]: GeoComply });
